import { useEffect, useState, useCallback } from "react";
import {
  Sparkles, Users2, PhoneCall, BadgeCheck, Trash2, Trophy, AlertTriangle,
  Percent, Download, Loader2, X, ArrowUpDown,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import api, { apiError } from "@/lib/api";
import { minDelay } from "@/lib/ux";
import { Loading, ErrorView, EmptyState } from "@/components/StateViews";
import { useAuth } from "@/contexts/AuthContext";
import DateRangePicker from "@/components/DateRangePicker";
import { toast } from "sonner";

const fmtMins = (m) => (m == null ? "—" : m < 60 ? `${m}m` : m < 1440 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`);
const pct = (v) => (v == null ? "—" : `${v}%`);
const scoreColor = (s) => (s == null ? "text-ink/40" : s >= 70 ? "text-emerald-600" : s >= 50 ? "text-amber-500" : "text-red-500");

const DIMENSIONS = [
  ["source", "Source"], ["campaign", "Campaign"], ["ad", "Ad"],
  ["form", "Form"], ["platform", "Platform"], ["segment", "Segment"],
];
const QUICK = [
  ["", "All"], ["execution_gap", "Good source, poor execution"],
  ["high_junk", "High junk"], ["low_quality", "Low quality"], ["high_quality", "High quality"],
];

const FLAG_STYLE = {
  "Strong Source": { cls: "bg-emerald-100 text-emerald-700", tip: "This source has strong lead quality and is being handled properly." },
  "Execution Gap": { cls: "bg-amber-100 text-amber-700", tip: "Good source quality, but low contact rate. Sales team may be under-working this source." },
  "Underworked Source": { cls: "bg-blue-100 text-blue-700", tip: "Too many leads from this source are not contacted, so quality cannot be judged confidently." },
  "High Junk Source": { cls: "bg-red-100 text-red-700", tip: "This source/campaign is producing a high percentage of junk leads. Campaign quality may be poor." },
};

function ChartCard({ title, children, empty }) {
  return (
    <div className="rounded-2xl border border-sky/70 bg-white p-4" data-testid={`chart-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <h3 className="mb-3 font-sora text-sm font-semibold text-navy">{title}</h3>
      {empty ? <div className="flex h-48 items-center justify-center text-xs text-ink/40">No data for the selected filters.</div> : <div className="h-48">{children}</div>}
    </div>
  );
}
const SimpleBar = ({ data, color = "#2F6BFF", angle = -20 }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#e8eef7" vertical={false} />
      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={angle} textAnchor={angle ? "end" : "middle"} height={angle ? 55 : 30} />
      <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
      <RTooltip />
      <Bar dataKey="value" radius={[4, 4, 0, 0]}>{data.map((e, i) => <Cell key={i} fill={color} />)}</Bar>
    </BarChart>
  </ResponsiveContainer>
);

export default function SourceQualityReport() {
  const { user, options } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [dimension, setDimension] = useState("source");
  const [filters, setFilters] = useState({ created_from: "", created_to: "", salesperson: "", source: "", campaign: "", stage: "", quality: "", service: "" });
  const [opts, setOpts] = useState({ salespersons: [], sources: [], campaigns: [], services: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [quick, setQuick] = useState("");
  const [sort, setSort] = useState({ key: "leads", dir: "desc" });
  const [exporting, setExporting] = useState(false);

  const clean = () => Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  useEffect(() => { api.get("/reports/filter-options").then(({ data }) => setOpts(data.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data } = await minDelay(api.get("/reports/source-quality", { params: { dimension, ...clean() } })); setData(data.data); }
    catch (e) { setError(apiError(e)); } finally { setLoading(false); }
  }, [dimension, filters]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  // Dynamic scoring engine (Phase 2) — additive, behind a toggle.
  const [dynOn, setDynOn] = useState(false);
  const [dynMap, setDynMap] = useState({});
  const [dynCfg, setDynCfg] = useState(null);
  useEffect(() => {
    if (!dynOn) return;
    api.get("/scoring/compute-batch", { params: { entity_type: "source", ...clean() } })
      .then(({ data }) => { setDynCfg(data.data.config); const m = {}; (data.data.rows || []).forEach((x) => { m[x.entity_id] = x; }); setDynMap(m); })
      .catch(() => setDynMap({}));
  }, [dynOn, filters]); // eslint-disable-line
  const dynTip = (b) => (b || []).map((x) => `${x.label}: ${x.available ? x.score : "no data"} (${x.weight}%)`).join("\n");
  const dynColor = (s) => (s == null ? "text-ink/40" : s >= 65 ? "text-emerald-600" : s >= 50 ? "text-amber-500" : "text-red-500");

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => { setFilters({ created_from: "", created_to: "", salesperson: "", source: "", campaign: "", stage: "", quality: "", service: "" }); setQuick(""); };
  const hasFilters = Object.values(filters).some(Boolean) || quick;
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/reports/source-quality/export", { params: { dimension, ...clean() }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `source_quality_${dimension}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Source quality exported");
    } catch (e) { toast.error(apiError(e)); }
    finally { setExporting(false); }
  };

  if (loading && !data) return <Loading label="Loading source quality..." />;
  if (error) return <ErrorView message={error} onRetry={load} />;

  const empty = !data || data.empty;
  const k = data?.kpis, c = data?.charts;
  let rows = data?.rows || [];
  if (quick === "execution_gap") rows = rows.filter((r) => r.execution_gap);
  else if (quick === "high_junk") rows = rows.filter((r) => r.junk_pct >= 20);
  else if (quick === "low_quality") rows = rows.filter((r) => r.quality_score != null && r.quality_score < 50);
  else if (quick === "high_quality") rows = rows.filter((r) => r.quality_score != null && r.quality_score >= 70);
  rows = [...rows].sort((a, b) => {
    const x = a[sort.key], y = b[sort.key];
    const cmp = sort.key === "name" ? String(x).localeCompare(String(y)) : ((Number(x) || 0) - (Number(y) || 0));
    return sort.dir === "desc" ? -cmp : cmp;
  });

  const cards = empty ? [] : [
    { label: "Total Leads", value: k.total_leads, icon: Users2, tone: "text-loop", bg: "bg-sky/50" },
    { label: "Contact Rate", value: pct(k.contact_rate), icon: PhoneCall, tone: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Qualification Rate", value: pct(k.qualification_rate), icon: BadgeCheck, tone: "text-navy", bg: "bg-mist" },
    { label: "Junk Rate", value: pct(k.junk_rate), icon: Trash2, tone: "text-red-500", bg: "bg-red-50" },
    { label: "Conversion", value: pct(k.conversion), icon: Percent, tone: "text-purple-500", bg: "bg-purple-50" },
    { label: "Best Quality Source", value: k.best_source ? k.best_source.name : "—", sub: k.best_source ? `Score ${k.best_source.score}` : "", icon: Trophy, tone: "text-amber-500", bg: "bg-amber-50" },
    { label: "Worst (Junk) Source", value: k.worst_source ? k.worst_source.name : "—", sub: k.worst_source ? `${k.worst_source.junk_pct}% junk` : "", icon: AlertTriangle, tone: "text-orange-500", bg: "bg-orange-50" },
    { label: "Execution Gaps", value: k.execution_gaps, icon: AlertTriangle, tone: "text-red-500", bg: "bg-red-50" },
  ];

  const COLS = [
    { key: "name", label: "Source", align: "left" },
    { key: "leads", label: "Leads" },
    { key: "contacted_pct", label: "Contacted %", suffix: "%" },
    { key: "nc_pct", label: "NC %", suffix: "%" },
    ...(dimension === "source" ? [
      { key: "not_picked_pct", label: "Not Picked %", suffix: "%" },
      { key: "call_back_later_pct", label: "Call Back Later %", suffix: "%" },
      { key: "yet_to_qualify_pct", label: "Yet To Qualify %", suffix: "%" },
    ] : []),
    { key: "avg_first_response_min", label: "Avg 1st Resp", mins: true },
    { key: "qualified_pct", label: "Qualified %", suffix: "%" },
    { key: "ace_king_pct", label: "Ace+King %", suffix: "%" },
    { key: "junk_pct", label: "Junk %", suffix: "%" },
    { key: "lost_pct", label: "Lost %", suffix: "%" },
    { key: "conversion", label: "Conversion %", suffix: "%" },
    { key: "quality_score", label: "Quality Score", score: true },
  ];

  return (
    <div className="space-y-6" data-testid="source-quality-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><Sparkles className="h-7 w-7 text-loop" />Source Quality</h1>
          <p className="flex items-center gap-2 text-sm text-ink/60">Which sources bring quality leads — and where good leads get wasted.{loading && data && <span className="flex items-center gap-1 text-loop"><Loader2 className="h-3.5 w-3.5 animate-spin" />Refreshing…</span>}</p>
        </div>
        {isAdmin && !empty && (
          <button onClick={doExport} disabled={exporting} data-testid="sq-export-btn" className="flex h-9 items-center gap-1.5 rounded-lg border border-sky px-3 text-sm font-medium text-navy hover:border-loop hover:text-loop disabled:opacity-60">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Export
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1 rounded-xl border border-sky/70 bg-white p-1" data-testid="sq-dimension-tabs">
        {DIMENSIONS.map(([key, label]) => (
          <button key={key} data-testid={`sq-dim-${key}`} onClick={() => setDimension(key)} className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${dimension === key ? "bg-loop text-white" : "text-ink/60 hover:bg-mist"}`}>{label}</button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-sky/70 bg-white p-3" data-testid="sq-filters">
        <DateRangePicker from={filters.created_from} to={filters.created_to} onChange={({ created_from, created_to }) => setFilters((f) => ({ ...f, created_from, created_to }))} testid="sq-date" />
        <select value={filters.salesperson} onChange={(e) => setF("salesperson", e.target.value)} data-testid="f-salesperson" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All salespersons</option>{opts.salespersons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        <select value={filters.source} onChange={(e) => setF("source", e.target.value)} data-testid="f-source" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All sources</option>{opts.sources.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={filters.campaign} onChange={(e) => setF("campaign", e.target.value)} data-testid="f-campaign" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All campaigns</option>{opts.campaigns.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={filters.stage} onChange={(e) => setF("stage", e.target.value)} data-testid="f-stage" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All stages</option>{(options?.stages || []).map((s) => <option key={s} value={s}>{options?.stage_meta?.[s]?.name || s}</option>)}</select>
        <select value={filters.quality} onChange={(e) => setF("quality", e.target.value)} data-testid="f-quality" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All quality</option>{(options?.qualities || []).map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={filters.service} onChange={(e) => setF("service", e.target.value)} data-testid="f-service" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All services</option>{opts.services.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        {hasFilters && <button onClick={clearFilters} data-testid="clear-filters" className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-medium text-ink/60 hover:bg-mist"><X className="h-3.5 w-3.5" />Clear</button>}
      </div>

      {empty ? (
        <EmptyState title="No report data found for the selected filters." />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="sq-kpis">
            {cards.map((t) => (
              <div key={t.label} className="rounded-2xl border border-sky/70 bg-white p-4">
                <div className={`mb-2 inline-flex rounded-lg p-2 ${t.bg}`}><t.icon className={`h-4 w-4 ${t.tone}`} /></div>
                <p className="truncate font-sora text-xl font-bold text-navy">{t.value}</p>
                <p className="text-xs text-ink/55">{t.label}{t.sub ? ` · ${t.sub}` : ""}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChartCard title="Leads by Source" empty={!c.leads_by_source.length}><SimpleBar data={c.leads_by_source} /></ChartCard>
            <ChartCard title="Source Quality Score" empty={!c.quality_score.length}><SimpleBar data={c.quality_score} color="#10b981" /></ChartCard>
            <ChartCard title="Contact Rate %" empty={!c.contact_rate.length}><SimpleBar data={c.contact_rate} color="#2F6BFF" /></ChartCard>
            <ChartCard title="Junk Rate %" empty={c.junk_rate.every((x) => !x.value)}><SimpleBar data={c.junk_rate} color="#ef4444" /></ChartCard>
          </div>
        </>
      )}

      {!empty && (
        <div className="rounded-2xl border border-sky/70 bg-white" data-testid="sq-table">
          <div className="flex flex-wrap items-center gap-1.5 border-b border-sky/60 p-3">
            {QUICK.map(([v, l]) => <button key={v} onClick={() => setQuick(v)} data-testid={`quick-${v || "all"}`} className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${quick === v ? "bg-loop text-white" : "border border-sky text-ink/60 hover:bg-mist"}`}>{l}</button>)}
            {dimension === "source" && (
              <button onClick={() => setDynOn((v) => !v)} data-testid="sq-dyn-toggle" title={dynCfg ? `${dynCfg.name} v${dynCfg.version}` : "Use the Admin-configured dynamic scoring engine"}
                className={`ml-auto h-8 rounded-lg px-3 text-xs font-semibold transition-colors ${dynOn ? "bg-loop text-white" : "border border-loop/40 text-loop hover:bg-loop/5"}`}>
                {dynOn ? `Dynamic engine: ON${dynCfg ? ` · v${dynCfg.version}` : ""}` : "Dynamic engine (beta)"}
              </button>
            )}
          </div>
          {!rows.length ? <EmptyState title="No sources match this filter." /> : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-mist/70 text-left text-ink/50"><tr>
                  {COLS.map((col) => (
                    <th key={col.key} onClick={() => toggleSort(col.key)} data-testid={`sq-sort-${col.key}`} className="cursor-pointer whitespace-nowrap px-3 py-2 font-semibold hover:text-loop">
                      <span className="inline-flex items-center gap-1">{col.label}<ArrowUpDown className={`h-3 w-3 ${sort.key === col.key ? "text-loop" : "text-ink/30"}`} /></span>
                    </th>
                  ))}
                  {dynOn && ["Dynamic", "Decision / Diagnosis"].map((h) => <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-sky/40">
                  {rows.map((r) => (
                    <tr key={r.name} className={`hover:bg-sky/10 ${r.execution_gap ? "bg-amber-50/40" : ""}`} data-testid={`sq-row-${r.name}`}>
                      {COLS.map((col) => (
                        <td key={col.key} className={`whitespace-nowrap px-3 py-2 ${col.align === "left" ? "font-medium text-navy" : "text-ink/70"} ${col.score ? `font-bold ${scoreColor(r.quality_score)}` : ""} ${col.key === "junk_pct" && r.junk_pct >= 20 ? "font-semibold text-red-500" : ""} ${col.key === "nc_pct" && r.nc_pct >= 40 ? "font-semibold text-red-500" : ""}`}>
                          {col.key === "name" ? (
                            <span className="flex flex-wrap items-center gap-1.5">{r.name}{(r.flag_labels || []).map((fl) => { const s = FLAG_STYLE[fl] || {}; return <span key={fl} title={s.tip} data-testid={`sq-flag-${r.name}-${fl.replace(/\s+/g, "-").toLowerCase()}`} className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${s.cls || "bg-mist text-ink/60"}`}>{fl}</span>; })}</span>
                          ) : col.mins ? fmtMins(r.avg_first_response_min)
                            : col.score ? (<><span>{r.quality_score == null ? "—" : r.quality_score}</span><span className="block text-[10px] font-medium text-ink/45">{r.quality_label}</span></>)
                              : `${r[col.key]}${col.suffix || ""}`}
                        </td>
                      ))}
                      {dynOn && (() => { const d = dynMap[r.name]; return (
                        <>
                          <td className={`whitespace-nowrap px-3 py-2 font-bold ${dynColor(d?.score)}`} title={d ? dynTip(d.breakdown) : "No dynamic score"} data-testid={`sq-dyn-${r.name}`}>
                            <span className="cursor-help">{d?.insufficient || d?.score == null ? "—" : d.score}</span>
                            <span className="block text-[10px] font-medium text-ink/45">{d?.label || "—"}</span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {(d?.flags || []).slice(0, 3).map((f) => (
                                <span key={f.key} title={`${f.diagnosis || ""}${f.recommended_action ? "\n→ " + f.recommended_action : ""}`}
                                  className={`cursor-help rounded px-1.5 py-0.5 text-[10px] font-semibold ${f.severity === "critical" ? "bg-red-50 text-red-600" : f.severity === "warning" ? "bg-amber-50 text-amber-600" : f.severity === "positive" ? "bg-emerald-50 text-emerald-600" : "bg-sky-50 text-sky-600"}`}>{f.label}</span>
                              ))}
                              {!(d?.flags || []).length && <span className="text-[10px] text-ink/30">—</span>}
                            </div>
                          </td>
                        </>
                      ); })()}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
